/**
 * FacturiTab - Modul Facturi (Emise și Primite)
 * Cu generator facturi și integrare cu devize
 */
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { formatDateRO } from "@/utils/dateFormat";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { 
  FileText, Plus, Trash2, Edit, Search, Eye, Download, 
  Receipt, Send, CheckCircle, Settings, Building2, 
  PlusCircle, Loader2, Package, Users, Globe, Code, 
  RefreshCw, Shield, CloudUpload, Clock, AlertTriangle,
  ShieldCheck, XCircle, Info, ExternalLink, Upload
} from "lucide-react";
import AnafDashboard from "./AnafDashboard";
import InvoiceTemplatesDialog from "./InvoiceTemplatesDialog";
import { generateInvoicePDF } from "@/utils/invoicePdf";
import { InvoiceList } from "./facturi";
import { checkAndDeductCredits } from "@/utils/creditSystem";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper pentru formatare numere
const formatNumber = (num) => {
  if (num === undefined || num === null || isNaN(num)) return "0,00";
  return new Intl.NumberFormat('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num);
};

export default function FacturiTab({ companyId, sites = [], devize = [] }) {
  const [activeTab, setActiveTab] = useState("emise");
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [showInvoiceTemplates, setShowInvoiceTemplates] = useState(false);
  const [showNewInvoice, setShowNewInvoice] = useState(false);
  const [showFromDeviz, setShowFromDeviz] = useState(false);
  const [clients, setClients] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [lookingUpCui, setLookingUpCui] = useState(false);
  const [showClientForm, setShowClientForm] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [showEditInvoice, setShowEditInvoice] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [newClient, setNewClient] = useState({ name: "", cui: "", reg_com: "", address: "", city: "", county: "", phone: "", email: "" });
  
  // ANAF verification state
  const [verifyingAnafCui, setVerifyingAnafCui] = useState(null);
  const [anafVerifyResult, setAnafVerifyResult] = useState(null);
  const [showAnafVerifyDialog, setShowAnafVerifyDialog] = useState(false);

  // ANAF e-Factura state
  const [showAnafSettings, setShowAnafSettings] = useState(false);
  const [showXmlPreview, setShowXmlPreview] = useState(false);
  const [xmlPreviewData, setXmlPreviewData] = useState(null);
  const [anafSettings, setAnafSettings] = useState({
    anaf_token: "",
    anaf_cui: "",
    anaf_environment: "test",
    auto_send_enabled: false,
    auto_send_days: 5,
    is_configured: false
  });
  const [sendingToAnaf, setSendingToAnaf] = useState(null);
  const [checkingAnafStatus, setCheckingAnafStatus] = useState(null);
  const [syncSpvDays, setSyncSpvDays] = useState(30);
  const [syncingSpv, setSyncingSpv] = useState(false);
  const [showAnafHelp, setShowAnafHelp] = useState(false);
  
  // Setări facturare
  const [settings, setSettings] = useState({
    series: "CRN",
    vat_percent: 21,
    default_due_days: 30,
    company_name: "",
    company_cui: "",
    company_reg_com: "",
    company_address: "",
    company_city: "",
    company_county: "",
    company_iban: "",
    company_bank: "",
    company_phone: "",
    company_email: ""
  });
  
  // Formular factură nouă
  const [newInvoice, setNewInvoice] = useState({
    invoice_date: new Date().toISOString().split('T')[0],
    due_date: "",
    client_name: "",
    client_cui: "",
    client_address: "",
    items: [{ name: "", um: "buc", quantity: 1, unit_price: 0, vat_percent: 21 }],
    notes: ""
  });

  // Fetch invoices
  const fetchInvoices = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API}/invoices?company_id=${companyId}&invoice_type=${activeTab === "emise" ? "emisa" : "primita"}`);
      setInvoices(response.data);
    } catch (error) {
      console.error("Error fetching invoices:", error);
    } finally {
      setLoading(false);
    }
  }, [companyId, activeTab]);

  // Fetch settings
  const fetchSettings = useCallback(async () => {
    if (!companyId) return;
    try {
      const response = await axios.get(`${API}/invoices/settings?company_id=${companyId}`);
      setSettings(response.data);
    } catch (error) {
      console.error("Error fetching settings:", error);
    }
  }, [companyId]);

  // Fetch clients
  const fetchClients = useCallback(async () => {
    if (!companyId) return;
    try {
      const response = await axios.get(`${API}/clients?company_id=${companyId}`);
      setClients(response.data);
    } catch (error) {
      console.error("Error fetching clients:", error);
    }
  }, [companyId]);

  // ANAF handlers
  const fetchAnafSettings = useCallback(async () => {
    if (!companyId) return;
    try {
      const response = await axios.get(`${API}/anaf/settings?company_id=${companyId}`);
      setAnafSettings(response.data);
    } catch (error) {
      console.error("Error fetching ANAF settings:", error);
    }
  }, [companyId]);

  // ANAF OAuth2 status + connect/disconnect
  const [anafOauthStatus, setAnafOauthStatus] = useState(null);
  const fetchAnafOauthStatus = useCallback(async () => {
    if (!companyId) return;
    try {
      const response = await axios.get(`${API}/anaf/oauth/status?company_id=${companyId}`);
      setAnafOauthStatus(response.data);
    } catch (error) {
      console.error("Error fetching ANAF OAuth status:", error);
    }
  }, [companyId]);

  const handleConnectAnafOauth = async () => {
    if (!anafSettings.anaf_cui) {
      toast.error("Introdu mai întâi CUI-ul firmei și salvează setările.");
      return;
    }
    try {
      const r = await axios.get(`${API}/anaf/oauth/start?company_id=${companyId}&cui=${encodeURIComponent(anafSettings.anaf_cui)}`);
      const url = r.data?.authorize_url;
      if (!url) { toast.error("Nu s-a putut obține URL-ul de autorizare."); return; }
      // Popup centralizat
      const w = 720, h = 760;
      const left = (window.screenX || 0) + (window.innerWidth - w) / 2;
      const top = (window.screenY || 0) + (window.innerHeight - h) / 2;
      const popup = window.open(url, "anaf-oauth", `width=${w},height=${h},left=${left},top=${top},menubar=no,toolbar=no,location=yes`);
      if (!popup) { toast.error("Browserul a blocat popup-ul. Permite-l și reîncearcă."); return; }
      toast.loading("Așteptăm autentificarea ANAF...", { id: "anaf-oauth-toast" });
      const handler = (e) => {
        if (!e.data || e.data.type !== "anaf-oauth") return;
        window.removeEventListener("message", handler);
        if (e.data.status === "ok") {
          toast.success("Conectat la ANAF cu succes!", { id: "anaf-oauth-toast" });
          fetchAnafSettings();
          fetchAnafOauthStatus();
        } else {
          toast.error(e.data.message || "Conexiune eșuată", { id: "anaf-oauth-toast" });
        }
      };
      window.addEventListener("message", handler);
      // Cleanup dacă userul închide popup-ul manual
      const checkClose = setInterval(() => {
        if (popup.closed) {
          clearInterval(checkClose);
          window.removeEventListener("message", handler);
          toast.dismiss("anaf-oauth-toast");
          fetchAnafOauthStatus();
        }
      }, 800);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Eroare la pornirea OAuth";
      toast.error(msg);
    }
  };

  const handleDisconnectAnafOauth = async () => {
    if (!window.confirm("Deconectezi OAuth ANAF? Tokenurile salvate vor fi șterse.")) return;
    try {
      await axios.post(`${API}/anaf/oauth/disconnect?company_id=${companyId}`);
      toast.success("Deconectat de la OAuth ANAF");
      fetchAnafSettings();
      fetchAnafOauthStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la deconectare");
    }
  };

  const handleRefreshAnafToken = async () => {
    try {
      const r = await axios.post(`${API}/anaf/oauth/refresh?company_id=${companyId}`);
      toast.success(`Token reîmprospătat (expiră ${r.data?.expires_at?.slice(0, 10)})`);
      fetchAnafSettings();
      fetchAnafOauthStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la refresh token");
    }
  };

  const handleSaveAnafSettings = async () => {
    try {
      await axios.put(`${API}/anaf/settings?company_id=${companyId}`, {
        anaf_token: anafSettings.anaf_token,
        anaf_cui: anafSettings.anaf_cui,
        anaf_environment: anafSettings.anaf_environment,
        auto_send_enabled: anafSettings.auto_send_enabled,
        auto_send_days: anafSettings.auto_send_days
      });
      toast.success("Setări ANAF salvate!");
      setShowAnafSettings(false);
      fetchAnafSettings();
    } catch (error) {
      toast.error("Eroare la salvare setări ANAF");
    }
  };

  const handleTestAnafConnection = async () => {
    try {
      const response = await axios.post(`${API}/anaf/test-connection?company_id=${companyId}`);
      if (response.data.success) {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message);
      }
      // Re-fetch to update last_test_at / last_test_success
      fetchAnafSettings();
    } catch (error) {
      toast.error("Eroare la testare conexiune ANAF");
    }
  };

  const handleDisconnectAnaf = async () => {
    if (!window.confirm("Sigur vrei să te deconectezi de la ANAF?\n\nVor fi șterse tokenul și CUI-ul configurat. Istoricul de facturi trimise/primite NU se șterge.")) {
      return;
    }
    try {
      await axios.delete(`${API}/anaf/settings?company_id=${companyId}`);
      toast.success("Deconectat de la ANAF");
      setAnafSettings({
        anaf_token: "",
        anaf_cui: "",
        anaf_environment: "test",
        auto_send_enabled: false,
        auto_send_days: 5,
        is_configured: false,
      });
      fetchAnafSettings();
    } catch (error) {
      toast.error("Eroare la deconectare ANAF");
    }
  };

  const handleSyncReceivedFromSpv = async () => {
    if (!anafSettings.is_configured) {
      toast.error("Configurează mai întâi tokenul ANAF");
      return;
    }
    const isTest = anafSettings.anaf_environment !== 'production';
    const days = Math.min(Math.max(parseInt(syncSpvDays) || 30, 1), 60);
    const confirmMsg = isTest
      ? `⚠️ MOD TEST\n\nVei prelua ${days} zile de facturi de pe SPV ANAF (mediu de TEST).\n\nFacturile primite vor fi cele simulate din mediul de test ANAF, NU facturile reale ale firmei. Continuă?`
      : `Vei prelua facturile primite de pe SPV ANAF din ultimele ${days} zile (mediu PRODUCȚIE).\n\nFacturile noi vor fi importate în secțiunea "Facturi Primite". Continuă?`;
    if (!window.confirm(confirmMsg)) return;

    setSyncingSpv(true);
    try {
      const response = await axios.post(
        `${API}/anaf/sync-received?company_id=${companyId}&days=${days}`
      );
      const r = response.data || {};
      if (r.success === false) {
        toast.error(`Sincronizare eșuată: ${r.message || 'eroare necunoscută'}`);
      } else {
        const imported = r.imported_count ?? r.imported ?? 0;
        const found = r.total_messages ?? r.messages_count ?? 0;
        toast.success(
          `Sincronizare OK: ${found} mesaje găsite, ${imported} facturi importate${isTest ? ' (mediu TEST)' : ''}`,
          { duration: 7000 }
        );
        fetchInvoices();
      }
    } catch (error) {
      const detail = error.response?.data?.detail || error.message;
      toast.error(`Eroare preluare SPV: ${detail}`);
    } finally {
      setSyncingSpv(false);
    }
  };

  const handleDownloadXML = async (invoice) => {
    try {
      const response = await axios.get(
        `${API}/invoices/${invoice.id}/xml?company_id=${companyId}`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const series = invoice.series || 'CRN';
      const number = String(invoice.number || 1).padStart(4, '0');
      link.setAttribute('download', `efactura_${series}${number}.xml`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("XML e-Factura descărcat!");
    } catch (error) {
      toast.error("Eroare la generare XML");
    }
  };

  const handlePreviewXML = async (invoice) => {
    try {
      const response = await axios.get(
        `${API}/invoices/${invoice.id}/xml/preview?company_id=${companyId}`
      );
      setXmlPreviewData(response.data);
      setShowXmlPreview(true);
    } catch (error) {
      toast.error("Eroare la previzualizare XML");
    }
  };

  const handleSendToAnaf = async (invoiceId) => {
    // Warn in TEST mode (anti-confusion)
    const isTest = anafSettings.anaf_environment !== 'production';
    if (isTest) {
      const ok = window.confirm(
        "⚠️ MOD TEST ANAF\n\n" +
        "Această factură va fi trimisă în mediul de TEST al ANAF.\n" +
        "NU ajunge la beneficiar și NU produce efecte fiscale reale.\n\n" +
        "Folosește acest mediu doar pentru a verifica conexiunea.\n" +
        "Pentru trimiteri reale, comută pe Producție în Setări ANAF.\n\n" +
        "Continui cu trimiterea în TEST?"
      );
      if (!ok) return;
    }
    setSendingToAnaf(invoiceId);
    try {
      const response = await axios.post(
        `${API}/invoices/${invoiceId}/anaf/send?company_id=${companyId}`
      );
      if (response.data.success) {
        toast.success((isTest ? '[TEST] ' : '') + (response.data.message || "Factură trimisă la ANAF!"));
        fetchInvoices();
      } else {
        const errors = response.data.errors;
        if (errors && errors.length > 0) {
          toast.error(`Erori: ${errors.join(', ')}`);
        } else {
          toast.error(response.data.message || "Eroare la trimitere ANAF");
        }
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(detail || "Eroare la trimitere ANAF");
    } finally {
      setSendingToAnaf(null);
    }
  };

  const handleCheckAnafStatus = async (invoiceId) => {
    setCheckingAnafStatus(invoiceId);
    try {
      const response = await axios.get(
        `${API}/invoices/${invoiceId}/anaf/status?company_id=${companyId}`
      );
      toast.info(`Status ANAF: ${response.data.anaf_status}`);
      fetchInvoices();
    } catch (error) {
      toast.error("Eroare la verificare status ANAF");
    } finally {
      setCheckingAnafStatus(null);
    }
  };

  // Lookup CUI - caută date firmă după CUI
  const handleLookupCui = async (cui) => {
    if (!cui || cui.length < 2) return;
    
    // Curăță CUI-ul (elimină RO, spații)
    const cleanCui = cui.replace(/[^0-9]/g, '');
    if (cleanCui.length < 2) return;
    
    setLookingUpCui(true);
    try {
      const response = await axios.get(`${API}/clients/lookup/${cleanCui}?company_id=${companyId}`);
      const { data, source, message } = response.data;
      
      // Verificăm dacă avem date reale (nu doar CUI)
      if (data && (data.name || data.denumire)) {
        setNewInvoice(prev => ({
          ...prev,
          client_name: data.name || data.denumire || prev.client_name,
          client_cui: data.cui || cleanCui,
          client_address: data.address || data.adresa || prev.client_address,
          client_city: data.city || data.localitate || "",
          client_county: data.county || data.judet || "",
          client_reg_com: data.reg_com || data.numar_reg_com || ""
        }));
        
        if (source === "local") {
          toast.success("Client găsit în baza de date!");
        } else if (source === "openapi.ro") {
          toast.success("Date firmă preluate automat!");
        }
      } else {
        // Nu am găsit date, afișăm mesaj informativ
        if (message) {
          toast.info(message);
        } else {
          toast.info("CUI negăsit. Completați manual datele.");
        }
        // Setăm cel puțin CUI-ul
        setNewInvoice(prev => ({
          ...prev,
          client_cui: cleanCui
        }));
      }
    } catch (error) {
      console.error("CUI lookup error:", error);
      toast.error("Eroare la căutare CUI");
    } finally {
      setLookingUpCui(false);
    }
  };

  // Șterge factură
  const handleDeleteInvoice = async (invoiceId) => {
    if (!window.confirm("Sigur doriți să ștergeți această factură?")) return;
    
    try {
      await axios.delete(`${API}/invoices/${invoiceId}?company_id=${companyId}`);
      toast.success("Factură ștearsă!");
      fetchInvoices();
    } catch (error) {
      toast.error("Eroare la ștergere factură");
    }
  };

  // Șterge client
  const handleDeleteClient = async (clientId) => {
    if (!window.confirm("Sigur doriți să ștergeți acest client?")) return;
    
    try {
      await axios.delete(`${API}/clients/${clientId}?company_id=${companyId}`);
      toast.success("Client șters!");
      fetchClients();
    } catch (error) {
      toast.error("Eroare la ștergere client");
    }
  };

  // Editează client
  const handleEditClient = (client) => {
    setEditingClient({ ...client });
    setShowClientForm(true);
  };

  // Salvează client (nou sau editare)
  const handleSaveClientFinal = async () => {
    const clientData = editingClient || newClient;
    if (!clientData.name || !clientData.cui) {
      toast.error("Completați numele și CUI-ul clientului");
      return;
    }
    
    try {
      if (editingClient?.id) {
        // Editare client existent
        await axios.put(`${API}/clients/${editingClient.id}?company_id=${companyId}`, clientData);
        toast.success("Client actualizat!");
      } else {
        // Client nou
        await axios.post(`${API}/clients?company_id=${companyId}`, clientData);
        toast.success("Client salvat!");
      }
      setShowClientForm(false);
      setEditingClient(null);
      setNewClient({ name: "", cui: "", reg_com: "", address: "", city: "", county: "", phone: "", email: "" });
      fetchClients();
    } catch (error) {
      toast.error("Eroare la salvare client");
    }
  };

  // Verifică datele firmei prin ANAF (demoanaf.ro)
  const handleVerifyAnaf = async (cui) => {
    if (!cui) {
      toast.error("Clientul nu are CUI completat");
      return;
    }
    setVerifyingAnafCui(cui);
    setAnafVerifyResult(null);
    setShowAnafVerifyDialog(true);

    try {
      const res = await axios.get(`${API}/clients/verify-anaf/${cui}?company_id=${companyId}`);
      setAnafVerifyResult(res.data);
    } catch (error) {
      const msg = error.response?.data?.detail || "Eroare la verificare ANAF";
      setAnafVerifyResult({ error: true, message: msg });
    } finally {
      setVerifyingAnafCui(null);
    }
  };

  // Editează factură
  const handleEditInvoice = (invoice) => {
    setEditingInvoice({
      ...invoice,
      items: invoice.items || [{ name: "", um: "buc", quantity: 1, unit_price: 0, vat_percent: 21 }]
    });
    setShowEditInvoice(true);
  };

  // Salvează factura editată
  const handleSaveEditedInvoice = async () => {
    if (!editingInvoice) return;
    
    // Verifică și deduce creditele pentru editare
    const creditResult = await checkAndDeductCredits(
      companyId,
      'edit_invoice',
      null,
      (warning) => toast.warning(warning, { duration: 8000 }),
      (block) => toast.error(block, { duration: 8000 })
    );
    
    if (!creditResult.canProceed) {
      return;
    }
    
    if (creditResult.usedBonus) {
      toast.warning("⚠️ Ați folosit documentul bonus gratuit!", { duration: 5000 });
    }
    
    try {
      const response = await axios.put(`${API}/invoices/${editingInvoice.id}?company_id=${companyId}`, {
        client_name: editingInvoice.client_name,
        client_cui: editingInvoice.client_cui,
        client_reg_com: editingInvoice.client_reg_com,
        client_address: editingInvoice.client_address,
        invoice_date: editingInvoice.invoice_date,
        due_date: editingInvoice.due_date,
        items: editingInvoice.items,
        notes: editingInvoice.notes
      });
      
      if (response.data.warning) {
        toast.warning(response.data.warning, { duration: 8000 });
      }
      toast.success("Factură actualizată!");
      setShowEditInvoice(false);
      setEditingInvoice(null);
      fetchInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Eroare la salvare factură");
    }
  };

  // Funcții helper pentru editare factură
  const updateEditItem = (index, field, value) => {
    setEditingInvoice(prev => ({
      ...prev,
      items: prev.items.map((item, i) => i === index ? { ...item, [field]: value } : item)
    }));
  };

  const addEditItem = () => {
    setEditingInvoice(prev => ({
      ...prev,
      items: [...prev.items, { name: "", um: "buc", quantity: 1, unit_price: 0, vat_percent: settings.vat_percent }]
    }));
  };

  const removeEditItem = (index) => {
    setEditingInvoice(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  // Calculează totaluri pentru editare
  const calculateEditTotals = (items) => {
    let subtotal = 0;
    let vatAmount = 0;
    
    items.forEach(item => {
      const itemTotal = (item.quantity || 0) * (item.unit_price || 0);
      const itemVat = itemTotal * ((item.vat_percent || 21) / 100);
      subtotal += itemTotal;
      vatAmount += itemVat;
    });
    
    return { subtotal, vatAmount, total: subtotal + vatAmount };
  };

  useEffect(() => {
    fetchInvoices();
    fetchSettings();
    fetchClients();
    fetchAnafSettings();
    fetchAnafOauthStatus();
  }, [fetchInvoices, fetchSettings, fetchClients, fetchAnafSettings, fetchAnafOauthStatus]);

  // Salvare setări
  const handleSaveSettings = async () => {
    try {
      await axios.put(`${API}/invoices/settings?company_id=${companyId}`, settings);
      toast.success("Setări salvate!");
      setShowSettings(false);
    } catch (error) {
      toast.error("Eroare la salvare setări");
    }
  };

  // Adaugă articol
  const addItem = () => {
    setNewInvoice(prev => ({
      ...prev,
      items: [...prev.items, { name: "", um: "buc", quantity: 1, unit_price: 0, vat_percent: settings.vat_percent }]
    }));
  };

  // Șterge articol
  const removeItem = (index) => {
    setNewInvoice(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  // Update articol
  const updateItem = (index, field, value) => {
    setNewInvoice(prev => ({
      ...prev,
      items: prev.items.map((item, i) => i === index ? { ...item, [field]: value } : item)
    }));
  };

  // Calculează totaluri
  const calculateTotals = (items) => {
    let subtotal = 0;
    let vatAmount = 0;
    
    items.forEach(item => {
      const itemTotal = (item.quantity || 0) * (item.unit_price || 0);
      const itemVat = itemTotal * ((item.vat_percent || 21) / 100);
      subtotal += itemTotal;
      vatAmount += itemVat;
    });
    
    return {
      subtotal: subtotal,
      vatAmount: vatAmount,
      total: subtotal + vatAmount
    };
  };

  // Crează factură
  const handleCreateInvoice = async () => {
    // Verifică și deduce creditele
    const creditResult = await checkAndDeductCredits(
      companyId,
      'generate_invoice',
      null,
      (warning) => toast.warning(warning, { duration: 8000 }),
      (block) => toast.error(block, { duration: 8000 })
    );
    
    if (!creditResult.canProceed) {
      return; // Blocat - credite insuficiente și bonus folosit
    }
    
    if (creditResult.usedBonus) {
      toast.warning("⚠️ Ați folosit documentul bonus gratuit!", { duration: 5000 });
    }
    
    try {
      const response = await axios.post(`${API}/invoices?company_id=${companyId}`, {
        invoice_type: activeTab === "emise" ? "emisa" : "primita",
        ...newInvoice
      });
      toast.success("Factură creată!");
      setShowNewInvoice(false);
      setNewInvoice({
        invoice_date: new Date().toISOString().split('T')[0],
        due_date: "",
        client_name: "",
        client_cui: "",
        client_address: "",
        items: [{ name: "", um: "buc", quantity: 1, unit_price: 0, vat_percent: settings.vat_percent }],
        notes: ""
      });
      fetchInvoices();
    } catch (error) {
      toast.error("Eroare la creare factură");
    }
  };

  // Emite factură din deviz
  const handleCreateFromDeviz = async (devizId) => {
    // Verifică și deduce creditele
    const creditResult = await checkAndDeductCredits(
      companyId,
      'generate_invoice',
      null,
      (warning) => toast.warning(warning, { duration: 8000 }),
      (block) => toast.error(block, { duration: 8000 })
    );
    
    if (!creditResult.canProceed) {
      return;
    }
    
    if (creditResult.usedBonus) {
      toast.warning("⚠️ Ați folosit documentul bonus gratuit!", { duration: 5000 });
    }
    
    try {
      const response = await axios.post(`${API}/invoices/from-deviz/${devizId}?company_id=${companyId}`);
      if (response.data.is_new) {
        toast.success("Factură generată din deviz!");
      } else {
        toast.info("Devizul are deja factură asociată");
      }
      setShowFromDeviz(false);
      fetchInvoices();
    } catch (error) {
      toast.error("Eroare la generare factură");
    }
  };

  // Emite factură
  const handleEmitInvoice = async (invoiceId) => {
    try {
      await axios.post(`${API}/invoices/${invoiceId}/emit?company_id=${companyId}`);
      toast.success("Factură emisă!");
      fetchInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Eroare la emitere");
    }
  };

  // Marchează plătită
  const handleMarkPaid = async (invoiceId) => {
    try {
      await axios.post(`${API}/invoices/${invoiceId}/mark-paid?company_id=${companyId}`);
      toast.success("Factură marcată ca plătită!");
      fetchInvoices();
    } catch (error) {
      toast.error("Eroare");
    }
  };

  // Download PDF — folosește preset-ul activ al firmei pentru aspect + logo
  const handleDownloadPDF = async (invoice) => {
    // Facturi externe → descarcă PDF-ul original (nu regenera)
    if (invoice?.source === "external") {
      try {
        const res = await axios.get(
          `${API}/invoices/${invoice.id}/external-pdf`,
          { params: { company_id: companyId }, responseType: "blob" },
        );
        const url = URL.createObjectURL(res.data);
        const a = document.createElement("a");
        a.href = url;
        a.download = invoice.external_pdf_filename || `factura_${invoice.invoice_number}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      } catch (e) {
        console.error(e);
        toast.error("Nu am putut descărca PDF-ul facturii externe");
      }
      return;
    }
    try {
      let preset = null;
      try {
        const r = await axios.get(`${API}/invoice-templates/active`, { params: { company_id: companyId } });
        preset = r.data;
      } catch { /* fall back to default 'classic' inside generator */ }
      const fileName = generateInvoicePDF(invoice, settings, preset);
      toast.success(`PDF generat: ${fileName}`);
    } catch (error) {
      console.error("PDF error:", error);
      toast.error("Eroare la generare PDF");
    }
  };

  // ============= Upload factură externă (emisă cu alt program) =============
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    file: null,
    invoice_number: "",
    invoice_date: new Date().toISOString().slice(0, 10),
    client_id: "",
    client_name: "",
    total_amount: "",
    notes: "",
  });
  const [uploadSubmitting, setUploadSubmitting] = useState(false);

  const resetUploadForm = () => setUploadForm({
    file: null,
    invoice_number: "",
    invoice_date: new Date().toISOString().slice(0, 10),
    client_id: "",
    client_name: "",
    total_amount: "",
    notes: "",
  });

  const handleUploadExternalInvoice = async () => {
    if (!uploadForm.file) { toast.error("Selectează un fișier PDF"); return; }
    if (!uploadForm.invoice_number.trim()) { toast.error("Introdu numărul facturii"); return; }
    if (!uploadForm.client_name.trim()) { toast.error("Introdu numele beneficiarului"); return; }
    const amount = parseFloat(uploadForm.total_amount);
    if (!Number.isFinite(amount) || amount <= 0) { toast.error("Introdu o sumă totală validă"); return; }
    try {
      setUploadSubmitting(true);
      const fd = new FormData();
      fd.append("file", uploadForm.file);
      fd.append("invoice_number", uploadForm.invoice_number.trim());
      fd.append("invoice_date", uploadForm.invoice_date);
      if (uploadForm.client_id) fd.append("client_id", uploadForm.client_id);
      fd.append("client_name", uploadForm.client_name.trim());
      fd.append("total_amount", String(amount));
      fd.append("notes", uploadForm.notes || "");
      await axios.post(`${API}/invoices/upload-external`, fd, {
        params: { company_id: companyId },
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Factură externă încărcată");
      setUploadDialogOpen(false);
      resetUploadForm();
      fetchInvoices();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nu am putut încărca factura");
    } finally {
      setUploadSubmitting(false);
    }
  };

  // Status badge
  const StatusBadge = ({ status }) => {
    const colors = {
      draft: "bg-gray-100 text-gray-600",
      emisa: "bg-blue-100 text-blue-600",
      platita: "bg-green-100 text-green-600",
      anulata: "bg-red-100 text-red-600"
    };
    const labels = {
      draft: "Ciornă",
      emisa: "Emisă",
      platita: "Plătită",
      anulata: "Anulată"
    };
    return (
      <Badge className={colors[status] || colors.draft}>
        {labels[status] || status}
      </Badge>
    );
  };

  // ANAF status badge
  const AnafStatusBadge = ({ invoice }) => {
    const anafStatus = invoice.anaf_status;
    if (!anafStatus || anafStatus === "netrimisa") return null;
    
    const config = {
      trimisa: { bg: "bg-amber-100 text-amber-700", label: "ANAF: Trimisă", icon: CloudUpload },
      in_procesare: { bg: "bg-blue-100 text-blue-700", label: "ANAF: Procesare", icon: Clock },
      acceptata: { bg: "bg-emerald-100 text-emerald-700", label: "ANAF: Acceptată", icon: CheckCircle },
      respinsa: { bg: "bg-red-100 text-red-700", label: "ANAF: Respinsă", icon: AlertTriangle },
      eroare: { bg: "bg-red-100 text-red-700", label: "ANAF: Eroare", icon: AlertTriangle },
    };
    
    const c = config[anafStatus] || { bg: "bg-gray-100 text-gray-600", label: `ANAF: ${anafStatus}`, icon: Globe };
    const Icon = c.icon;
    
    return (
      <Badge className={`${c.bg} text-xs ml-1`} data-testid="anaf-status-badge">
        <Icon className="w-3 h-3 mr-1" />
        {c.label}
      </Badge>
    );
  };

  const totals = calculateTotals(newInvoice.items);

  return (
    <div className="space-y-4">
      {anafSettings.is_configured && anafSettings.anaf_environment !== 'production' && (
        <div
          className="rounded-lg border-2 border-amber-400 bg-gradient-to-r from-amber-50 to-orange-50 p-3 sm:p-4 flex items-start gap-3 shadow-sm"
          data-testid="anaf-test-mode-banner"
        >
          <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-amber-900 text-sm sm:text-base">
              MOD TEST ANAF e-Factura activ
            </div>
            <div className="text-xs sm:text-sm text-amber-800 mt-0.5">
              Facturile trimise prin „Trimite la ANAF" și preluările de pe SPV folosesc mediul de <strong>simulare</strong> al ANAF.
              <strong> NU ajung la beneficiar și NU au efect fiscal real.</strong> Comută pe „Producție" în Setări ANAF după ce ai validat conexiunea.
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="border-amber-400 text-amber-800 hover:bg-amber-100 flex-shrink-0"
            onClick={() => setShowAnafSettings(true)}
            data-testid="anaf-test-banner-settings-btn"
          >
            <Settings className="w-4 h-4 mr-1" />
            Setări
          </Button>
        </div>
      )}
      <Card className="border-0 shadow-lg">
        <CardHeader>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2 text-xl sm:text-2xl font-bold" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
                <Receipt className="w-6 h-6 text-purple-600" />
                Facturi
              </CardTitle>
              <CardDescription>Gestionare facturi emise și primite</CardDescription>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button variant="outline" onClick={() => setShowSettings(true)} data-testid="invoice-settings-btn">
                <Settings className="w-4 h-4 mr-2" />
                Setări
              </Button>
              <Button 
                variant="outline" 
                onClick={() => setShowAnafSettings(true)} 
                data-testid="anaf-settings-btn"
                className={
                  anafSettings.is_configured
                    ? (anafSettings.anaf_environment === 'production'
                        ? "border-green-300 text-green-700"
                        : "border-amber-400 text-amber-800 bg-amber-50")
                    : "border-amber-300 text-amber-700"
                }
              >
                <Globe className="w-4 h-4 mr-2" />
                ANAF e-Factura
                {anafSettings.is_configured && anafSettings.anaf_environment === 'production' && (
                  <CheckCircle className="w-3 h-3 ml-1 text-green-600" />
                )}
                {anafSettings.is_configured && anafSettings.anaf_environment !== 'production' && (
                  <Badge className="ml-2 bg-amber-200 text-amber-900 border-amber-400 text-[10px] font-bold" data-testid="anaf-test-badge">
                    TEST
                  </Badge>
                )}
              </Button>
              <Button onClick={() => setShowFromDeviz(true)} className="bg-green-600 hover:bg-green-700" data-testid="invoice-from-deviz-btn">
                <FileText className="w-4 h-4 mr-2" />
                Din Deviz
              </Button>
              <Button onClick={() => setUploadDialogOpen(true)} variant="outline" className="border-teal-300 text-teal-700 hover:bg-teal-50" data-testid="upload-external-invoice-btn">
                <Upload className="w-4 h-4 mr-2" />
                Încarcă factură
              </Button>
              <Button onClick={() => setShowNewInvoice(true)} className="bg-purple-600 hover:bg-purple-700" data-testid="new-invoice-btn">
                <Plus className="w-4 h-4 mr-2" />
                Factură Nouă
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <div className="overflow-x-auto -mx-2 px-2 mb-4 facturi-tabs-scroll">
              <TabsList className="inline-flex w-max min-w-full gap-1">
                <TabsTrigger value="emise" data-testid="tab-emise" className="flex flex-col sm:flex-row items-center px-2.5 sm:px-3 py-2 h-auto">
                  <Send className="w-4 h-4 sm:mr-2" />
                  <span className="text-[10px] sm:text-sm leading-tight">Emise</span>
                </TabsTrigger>
                <TabsTrigger value="primite" data-testid="tab-primite" className="flex flex-col sm:flex-row items-center px-2.5 sm:px-3 py-2 h-auto">
                  <Receipt className="w-4 h-4 sm:mr-2" />
                  <span className="text-[10px] sm:text-sm leading-tight">Primite</span>
                </TabsTrigger>
                <TabsTrigger value="anaf" data-testid="tab-anaf" className="flex flex-col sm:flex-row items-center px-2.5 sm:px-3 py-2 h-auto text-teal-700">
                  <Globe className="w-4 h-4 sm:mr-2" />
                  <span className="text-[10px] sm:text-sm leading-tight">ANAF</span>
                </TabsTrigger>
                <TabsTrigger value="clienti" data-testid="tab-clienti" className="flex flex-col sm:flex-row items-center px-2.5 sm:px-3 py-2 h-auto">
                  <Users className="w-4 h-4 sm:mr-2" />
                  <span className="text-[10px] sm:text-sm leading-tight">Clienți</span>
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="emise">
              <InvoiceList 
                invoices={invoices}
                loading={loading}
                onEmit={handleEmitInvoice}
                onPaid={handleMarkPaid}
                onDelete={handleDeleteInvoice}
                onView={setSelectedInvoice}
                onEdit={handleEditInvoice}
                onDownloadPDF={handleDownloadPDF}
                onDownloadXML={handleDownloadXML}
                onPreviewXML={handlePreviewXML}
                onSendToAnaf={handleSendToAnaf}
                onCheckAnafStatus={handleCheckAnafStatus}
                sendingToAnaf={sendingToAnaf}
                checkingAnafStatus={checkingAnafStatus}
                anafConfigured={anafSettings.is_configured}
                StatusBadge={StatusBadge}
                AnafStatusBadge={AnafStatusBadge}
              />
            </TabsContent>

            <TabsContent value="primite">
              <InvoiceList 
                invoices={invoices}
                loading={loading}
                onEmit={handleEmitInvoice}
                onPaid={handleMarkPaid}
                onDelete={handleDeleteInvoice}
                onView={setSelectedInvoice}
                onEdit={handleEditInvoice}
                onDownloadPDF={handleDownloadPDF}
                onDownloadXML={handleDownloadXML}
                onPreviewXML={handlePreviewXML}
                StatusBadge={StatusBadge}
                AnafStatusBadge={AnafStatusBadge}
                isPrimite
              />
            </TabsContent>

            <TabsContent value="anaf">
              <AnafDashboard companyId={companyId} />
            </TabsContent>

            <TabsContent value="clienti">
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold">Lista Clienți</h3>
                  <Button onClick={() => setShowClientForm(true)} className="bg-purple-600 hover:bg-purple-700">
                    <Plus className="w-4 h-4 mr-2" />
                    Client Nou
                  </Button>
                </div>
                
                {clients.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>Nu există clienți salvați</p>
                    <p className="text-sm">Clienții vor fi adăugați automat când emiți facturi sau îi poți adăuga manual</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Denumire</TableHead>
                        <TableHead>CUI</TableHead>
                        <TableHead>Adresă</TableHead>
                        <TableHead>Contact</TableHead>
                        <TableHead className="text-right">Acțiuni</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {clients.map(client => (
                        <TableRow key={client.id}>
                          <TableCell className="font-medium">{client.name}</TableCell>
                          <TableCell>{client.cui || "-"}</TableCell>
                          <TableCell className="max-w-[200px] truncate">
                            {client.address ? `${client.address}${client.city ? `, ${client.city}` : ""}` : "-"}
                          </TableCell>
                          <TableCell>
                            {client.phone || client.email ? (
                              <div className="text-xs">
                                {client.phone && <p>{client.phone}</p>}
                                {client.email && <p className="text-blue-600">{client.email}</p>}
                              </div>
                            ) : "-"}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex gap-1 justify-end">
                              {client.cui && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  data-testid={`verify-anaf-btn-${client.id}`}
                                  onClick={() => handleVerifyAnaf(client.cui)}
                                  className="text-teal-600 hover:text-teal-700 hover:bg-teal-50"
                                  title="Verifică ANAF"
                                >
                                  <ShieldCheck className="w-4 h-4" />
                                </Button>
                              )}
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handleEditClient(client)}
                                className="text-blue-500 hover:text-blue-700 hover:bg-blue-50"
                                title="Editează"
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handleDeleteClient(client.id)}
                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                title="Șterge"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Dialog Setări Facturare */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Setări Model Facturare
            </DialogTitle>
            <DialogDescription>
              Completați datele firmei care vor apărea pe facturi și alte documente
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Serie Factură</Label>
                <Input 
                  value={settings.series}
                  onChange={(e) => setSettings({...settings, series: e.target.value.toUpperCase()})}
                  placeholder="CRN"
                />
              </div>
              <div>
                <Label>Număr Factură (start)</Label>
                <Input 
                  type="number"
                  min="1"
                  value={settings.next_number || ""}
                  onChange={(e) => setSettings({...settings, next_number: parseInt(e.target.value) || 1})}
                  placeholder="1"
                  data-testid="invoice-start-number"
                />
                <p className="text-xs text-slate-500 mt-1">Următoarea factură va avea acest număr</p>
              </div>
              <div>
                <Label>TVA Implicit (%)</Label>
                <Input 
                  type="number"
                  value={settings.vat_percent}
                  onChange={(e) => setSettings({...settings, vat_percent: parseFloat(e.target.value) || 21})}
                />
              </div>
            </div>

            <div className="border-t pt-4">
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                <Building2 className="w-4 h-4" />
                Date Firmă (Furnizor)
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Denumire Firmă *</Label>
                  <Input 
                    value={settings.company_name}
                    onChange={(e) => setSettings({...settings, company_name: e.target.value})}
                    placeholder="SC EXEMPLU SRL"
                  />
                </div>
                <div>
                  <Label>CUI/CIF *</Label>
                  <Input 
                    value={settings.company_cui}
                    onChange={(e) => setSettings({...settings, company_cui: e.target.value})}
                    placeholder="RO12345678"
                  />
                </div>
                <div>
                  <Label>Nr. Reg. Comerț</Label>
                  <Input 
                    value={settings.company_reg_com}
                    onChange={(e) => setSettings({...settings, company_reg_com: e.target.value})}
                    placeholder="J40/1234/2020"
                  />
                </div>
                <div className="col-span-2">
                  <Label>Adresă</Label>
                  <Input 
                    value={settings.company_address}
                    onChange={(e) => setSettings({...settings, company_address: e.target.value})}
                    placeholder="Str. Exemplu nr. 1"
                  />
                </div>
                <div>
                  <Label>Oraș</Label>
                  <Input 
                    value={settings.company_city}
                    onChange={(e) => setSettings({...settings, company_city: e.target.value})}
                    placeholder="București"
                  />
                </div>
                <div>
                  <Label>Județ</Label>
                  <Input 
                    value={settings.company_county}
                    onChange={(e) => setSettings({...settings, company_county: e.target.value})}
                    placeholder="București"
                  />
                </div>
                <div>
                  <Label>IBAN</Label>
                  <Input 
                    value={settings.company_iban}
                    onChange={(e) => setSettings({...settings, company_iban: e.target.value})}
                    placeholder="RO49AAAA1B31007593840000"
                  />
                </div>
                <div>
                  <Label>Banca</Label>
                  <Input 
                    value={settings.company_bank}
                    onChange={(e) => setSettings({...settings, company_bank: e.target.value})}
                    placeholder="Banca Transilvania"
                  />
                </div>
                <div>
                  <Label>Telefon</Label>
                  <Input 
                    value={settings.company_phone}
                    onChange={(e) => setSettings({...settings, company_phone: e.target.value})}
                    placeholder="0721 123 456"
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input 
                    value={settings.company_email}
                    onChange={(e) => setSettings({...settings, company_email: e.target.value})}
                    placeholder="contact@exemplu.ro"
                  />
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => { setShowSettings(false); setShowInvoiceTemplates(true); }}
                className="border-purple-300 text-purple-700 hover:bg-purple-50 w-full sm:w-auto"
                data-testid="open-invoice-templates-btn"
              >
                <FileText className="w-4 h-4 mr-2" />
                Personalizare șablon factură
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowSettings(false)}>Anulează</Button>
                <Button onClick={handleSaveSettings} className="bg-purple-600 hover:bg-purple-700">
                  Salvează Setări
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Personalizare șablon factură */}
      <InvoiceTemplatesDialog
        open={showInvoiceTemplates}
        onOpenChange={setShowInvoiceTemplates}
        companyId={companyId}
        companyLogoUrl={settings.logo_url || settings.company_logo || ""}
      />

      {/* Dialog Încarcă factură externă (PDF emis cu alt program) */}
      <Dialog open={uploadDialogOpen} onOpenChange={(v) => { setUploadDialogOpen(v); if (!v) resetUploadForm(); }}>
        <DialogContent className="max-w-lg" data-testid="upload-external-invoice-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-teal-600" /> Încarcă factură externă (PDF)
            </DialogTitle>
            <DialogDescription>
              Pentru facturi emise cu alt program. Clientul o va putea descărca din portalul lui.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Fișier PDF *</Label>
              <Input
                type="file"
                accept="application/pdf,.pdf"
                onChange={(e) => setUploadForm((s) => ({ ...s, file: e.target.files?.[0] || null }))}
                data-testid="upload-external-file-input"
              />
              {uploadForm.file && (
                <div className="text-xs text-slate-500 mt-1">
                  {uploadForm.file.name} · {(uploadForm.file.size / 1024).toFixed(1)} KB
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Nr. factură *</Label>
                <Input
                  value={uploadForm.invoice_number}
                  onChange={(e) => setUploadForm((s) => ({ ...s, invoice_number: e.target.value }))}
                  placeholder="Ex: EXT-2026-001"
                  data-testid="upload-external-number-input"
                />
              </div>
              <div>
                <Label>Dată emitere *</Label>
                <Input
                  type="date"
                  value={uploadForm.invoice_date}
                  onChange={(e) => setUploadForm((s) => ({ ...s, invoice_date: e.target.value }))}
                  data-testid="upload-external-date-input"
                />
              </div>
            </div>
            <div>
              <Label>Beneficiar (client) *</Label>
              <Select
                value={uploadForm.client_id || "__none__"}
                onValueChange={(v) => {
                  if (v === "__none__") return setUploadForm((s) => ({ ...s, client_id: "", client_name: s.client_name }));
                  const c = clients.find((c) => c.id === v);
                  setUploadForm((s) => ({ ...s, client_id: v, client_name: c?.name || s.client_name }));
                }}
              >
                <SelectTrigger data-testid="upload-external-client-select"><SelectValue placeholder="Alege client sau introdu manual" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Introduce manual —</SelectItem>
                  {clients.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                className="mt-2"
                value={uploadForm.client_name}
                onChange={(e) => setUploadForm((s) => ({ ...s, client_name: e.target.value }))}
                placeholder="Nume beneficiar"
                data-testid="upload-external-clientname-input"
              />
            </div>
            <div>
              <Label>Sumă totală (RON) *</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={uploadForm.total_amount}
                onChange={(e) => setUploadForm((s) => ({ ...s, total_amount: e.target.value }))}
                placeholder="Ex: 1250.50"
                data-testid="upload-external-amount-input"
              />
            </div>
            <div>
              <Label>Observații</Label>
              <Textarea
                rows={2}
                value={uploadForm.notes}
                onChange={(e) => setUploadForm((s) => ({ ...s, notes: e.target.value }))}
                placeholder="Opțional"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setUploadDialogOpen(false)} disabled={uploadSubmitting}>Anulează</Button>
            <Button
              onClick={handleUploadExternalInvoice}
              disabled={uploadSubmitting || !uploadForm.file}
              className="bg-teal-600 hover:bg-teal-700 text-white"
              data-testid="upload-external-submit-btn"
            >
              {uploadSubmitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
              Încarcă factura
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Factură Nouă */}
      <Dialog open={showNewInvoice} onOpenChange={setShowNewInvoice}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Factură Nouă</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Date factură */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Data Emiterii *</Label>
                <Input 
                  type="date"
                  value={newInvoice.invoice_date}
                  onChange={(e) => setNewInvoice({...newInvoice, invoice_date: e.target.value})}
                />
              </div>
              <div>
                <Label>Data Scadență</Label>
                <Input 
                  type="date"
                  value={newInvoice.due_date}
                  onChange={(e) => setNewInvoice({...newInvoice, due_date: e.target.value})}
                />
              </div>
              <div>
                <Label>Client din listă</Label>
                <Select onValueChange={(id) => {
                  const client = clients.find(c => c.id === id);
                  if (client) {
                    setNewInvoice({
                      ...newInvoice,
                      client_name: client.name,
                      client_cui: client.cui || "",
                      client_address: client.address || ""
                    });
                  }
                }}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selectează client..." />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Date client */}
            <div className="border-t pt-4">
              <h4 className="font-semibold mb-3">Date Client</h4>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>CUI Client *</Label>
                  <div className="flex gap-2">
                    <Input 
                      value={newInvoice.client_cui}
                      onChange={(e) => setNewInvoice({...newInvoice, client_cui: e.target.value.toUpperCase()})}
                      placeholder="12345678"
                      onBlur={(e) => handleLookupCui(e.target.value)}
                    />
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="icon"
                      onClick={() => handleLookupCui(newInvoice.client_cui)}
                      disabled={lookingUpCui}
                      title="Caută date firmă după CUI"
                    >
                      {lookingUpCui ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">Introduceți CUI și datele se vor completa automat</p>
                </div>
                <div>
                  <Label>Nume Client *</Label>
                  <Input 
                    value={newInvoice.client_name}
                    onChange={(e) => setNewInvoice({...newInvoice, client_name: e.target.value})}
                    placeholder="Denumire firmă client"
                  />
                </div>
                <div>
                  <Label>Nr. Reg. Comerț</Label>
                  <Input 
                    value={newInvoice.client_reg_com || ""}
                    onChange={(e) => setNewInvoice({...newInvoice, client_reg_com: e.target.value})}
                    placeholder="J40/1234/2020"
                  />
                </div>
                <div className="col-span-2">
                  <Label>Adresă Client</Label>
                  <Input 
                    value={newInvoice.client_address}
                    onChange={(e) => setNewInvoice({...newInvoice, client_address: e.target.value})}
                    placeholder="Adresa completă"
                  />
                </div>
                <div>
                  <Label>Oraș / Județ</Label>
                  <Input 
                    value={`${newInvoice.client_city || ""} ${newInvoice.client_county ? `/ ${newInvoice.client_county}` : ""}`}
                    onChange={(e) => setNewInvoice({...newInvoice, client_city: e.target.value})}
                    placeholder="București / Sector 1"
                  />
                </div>
              </div>
            </div>

            {/* Articole */}
            <div className="border-t pt-4">
              <div className="flex justify-between items-center mb-3">
                <h4 className="font-semibold">Articole</h4>
                <Button size="sm" variant="outline" onClick={addItem}>
                  <Plus className="w-4 h-4 mr-1" />
                  Articol
                </Button>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[300px]">Denumire</TableHead>
                    <TableHead>U.M.</TableHead>
                    <TableHead>Cant.</TableHead>
                    <TableHead>Preț unit.</TableHead>
                    <TableHead>TVA %</TableHead>
                    <TableHead>Total</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {newInvoice.items.map((item, idx) => (
                    <TableRow key={`new-item-${idx}-${item.name}`}>
                      <TableCell>
                        <Input 
                          value={item.name}
                          onChange={(e) => updateItem(idx, 'name', e.target.value)}
                          placeholder="Denumire produs/serviciu"
                        />
                      </TableCell>
                      <TableCell>
                        <Input 
                          value={item.um}
                          onChange={(e) => updateItem(idx, 'um', e.target.value)}
                          className="w-16"
                        />
                      </TableCell>
                      <TableCell>
                        <Input 
                          type="number"
                          value={item.quantity}
                          onChange={(e) => updateItem(idx, 'quantity', parseFloat(e.target.value) || 0)}
                          className="w-20"
                        />
                      </TableCell>
                      <TableCell>
                        <Input 
                          type="number"
                          value={item.unit_price}
                          onChange={(e) => updateItem(idx, 'unit_price', parseFloat(e.target.value) || 0)}
                          className="w-24"
                        />
                      </TableCell>
                      <TableCell>
                        <Input 
                          type="number"
                          value={item.vat_percent}
                          onChange={(e) => updateItem(idx, 'vat_percent', parseFloat(e.target.value) || 0)}
                          className="w-16"
                        />
                      </TableCell>
                      <TableCell className="font-medium">
                        {formatNumber(item.quantity * item.unit_price)} RON
                      </TableCell>
                      <TableCell>
                        {newInvoice.items.length > 1 && (
                          <Button size="sm" variant="ghost" onClick={() => removeItem(idx)} className="text-red-500">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Totaluri */}
              <div className="flex justify-end mt-4">
                <div className="w-64 space-y-2">
                  <div className="flex justify-between">
                    <span>Subtotal:</span>
                    <span className="font-medium">{formatNumber(totals.subtotal)} RON</span>
                  </div>
                  <div className="flex justify-between">
                    <span>TVA:</span>
                    <span className="font-medium">{formatNumber(totals.vatAmount)} RON</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold border-t pt-2">
                    <span>TOTAL:</span>
                    <span>{formatNumber(totals.total)} RON</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Note */}
            <div>
              <Label>Observații</Label>
              <Textarea 
                value={newInvoice.notes}
                onChange={(e) => setNewInvoice({...newInvoice, notes: e.target.value})}
                placeholder="Observații suplimentare..."
                rows={2}
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowNewInvoice(false)}>Anulează</Button>
              <Button onClick={handleCreateInvoice} className="bg-purple-600 hover:bg-purple-700">
                Salvează Factură
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Generare din Deviz */}
      <Dialog open={showFromDeviz} onOpenChange={setShowFromDeviz}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-green-600" />
              Emite Factură după Deviz
            </DialogTitle>
            <DialogDescription>
              Selectează un deviz pentru a genera automat factura
            </DialogDescription>
          </DialogHeader>
          
          <ScrollArea className="max-h-[400px]">
            <div className="space-y-2">
              {devize.length === 0 ? (
                <p className="text-center text-slate-500 py-8">Nu există devize disponibile</p>
              ) : (
                devize.map(deviz => (
                  <div 
                    key={deviz.id}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 cursor-pointer"
                    onClick={() => handleCreateFromDeviz(deviz.id)}
                  >
                    <div>
                      <p className="font-medium">{deviz.numar_deviz || deviz.numarDeviz}</p>
                      <p className="text-sm text-slate-500">{deviz.beneficiar || "Fără beneficiar"}</p>
                      <p className="text-xs text-slate-400">
                        Total: {formatNumber(deviz.totals?.totalCuTVA || 0)} RON
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {deviz.invoice_id ? (
                        <Badge variant="outline" className="text-green-600 border-green-200 bg-green-50">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Facturat
                        </Badge>
                      ) : (
                        <Button size="sm" className="bg-green-600 hover:bg-green-700">
                          <Receipt className="w-4 h-4 mr-1" />
                          Facturează
                        </Button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Dialog Vizualizare Factură */}
      <Dialog open={!!selectedInvoice} onOpenChange={(open) => !open && setSelectedInvoice(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-purple-600" />
              Factură {selectedInvoice?.series}/{selectedInvoice?.number}
            </DialogTitle>
            <DialogDescription>Detaliile complete ale facturii</DialogDescription>
          </DialogHeader>
          
          {selectedInvoice && (
            <div className="space-y-6">
              {/* Header cu status */}
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-2xl font-bold text-slate-800">
                    Seria {selectedInvoice.series} Nr. {selectedInvoice.number}
                  </h2>
                  <p className="text-slate-500">Data: {formatDateRO(selectedInvoice.invoice_date)}</p>
                  {selectedInvoice.due_date && (
                    <p className="text-slate-500">Scadenta: {formatDateRO(selectedInvoice.due_date)}</p>
                  )}
                </div>
                <StatusBadge status={selectedInvoice.status} />
              </div>
              
              {/* Furnizor și Client */}
              <div className="grid grid-cols-2 gap-6">
                <div className="p-4 bg-blue-50 rounded-lg">
                  <h3 className="font-semibold text-blue-700 mb-2">FURNIZOR</h3>
                  <p className="font-medium">{selectedInvoice.supplier_name || settings.company_name}</p>
                  {(selectedInvoice.supplier_cui || settings.company_cui) && (
                    <p className="text-sm text-slate-600">CUI: {selectedInvoice.supplier_cui || settings.company_cui}</p>
                  )}
                  {(selectedInvoice.supplier_reg_com || settings.company_reg_com) && (
                    <p className="text-sm text-slate-600">Reg.Com: {selectedInvoice.supplier_reg_com || settings.company_reg_com}</p>
                  )}
                  {(selectedInvoice.supplier_address || settings.company_address) && (
                    <p className="text-sm text-slate-600">{selectedInvoice.supplier_address || settings.company_address}</p>
                  )}
                </div>
                
                <div className="p-4 bg-green-50 rounded-lg">
                  <h3 className="font-semibold text-green-700 mb-2">CLIENT</h3>
                  <p className="font-medium">{selectedInvoice.client_name}</p>
                  {selectedInvoice.client_cui && (
                    <p className="text-sm text-slate-600">CUI: {selectedInvoice.client_cui}</p>
                  )}
                  {selectedInvoice.client_reg_com && (
                    <p className="text-sm text-slate-600">Reg.Com: {selectedInvoice.client_reg_com}</p>
                  )}
                  {selectedInvoice.client_address && (
                    <p className="text-sm text-slate-600">{selectedInvoice.client_address}</p>
                  )}
                </div>
              </div>
              
              {/* Tabel produse/servicii */}
              <div>
                <h3 className="font-semibold mb-2">Produse / Servicii</h3>
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-100">
                      <TableHead>Descriere</TableHead>
                      <TableHead className="text-center">UM</TableHead>
                      <TableHead className="text-right">Cant.</TableHead>
                      <TableHead className="text-right">Preț unit.</TableHead>
                      <TableHead className="text-right">TVA %</TableHead>
                      <TableHead className="text-right">Valoare</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(selectedInvoice.items || []).map((item, idx) => (
                      <TableRow key={`view-item-${idx}-${item.name}`}>
                        <TableCell>{item.name || item.description}</TableCell>
                        <TableCell className="text-center">{item.um || item.unit || 'buc'}</TableCell>
                        <TableCell className="text-right">{item.quantity}</TableCell>
                        <TableCell className="text-right">{formatNumber(item.unit_price || item.price)} RON</TableCell>
                        <TableCell className="text-right">{item.vat_percent || 21}%</TableCell>
                        <TableCell className="text-right font-medium">
                          {formatNumber((item.quantity || 1) * (item.unit_price || item.price || 0))} RON
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              
              {/* Totaluri */}
              <div className="flex justify-end">
                <div className="w-64 space-y-2">
                  <div className="flex justify-between">
                    <span>Subtotal:</span>
                    <span>{formatNumber(selectedInvoice.subtotal || 0)} RON</span>
                  </div>
                  <div className="flex justify-between text-slate-600">
                    <span>TVA:</span>
                    <span>{formatNumber(selectedInvoice.vat_amount || 0)} RON</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold border-t pt-2">
                    <span>TOTAL:</span>
                    <span className="text-green-600">{formatNumber(selectedInvoice.total || 0)} RON</span>
                  </div>
                </div>
              </div>
              
              {/* Note */}
              {selectedInvoice.notes && (
                <div className="p-3 bg-amber-50 rounded-lg">
                  <h4 className="font-medium text-amber-800 mb-1">Observații:</h4>
                  <p className="text-sm text-amber-700">{selectedInvoice.notes}</p>
                </div>
              )}
              
              {/* Butoane acțiuni */}
              <div className="flex gap-2 justify-end border-t pt-4 flex-wrap">
                <Button 
                  variant="outline" 
                  onClick={() => handleDownloadPDF(selectedInvoice)}
                >
                  <Download className="w-4 h-4 mr-2" />
                  PDF
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => handleDownloadXML(selectedInvoice)}
                  className="text-teal-700 border-teal-300 hover:bg-teal-50"
                >
                  <Code className="w-4 h-4 mr-2" />
                  XML e-Factura
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => handlePreviewXML(selectedInvoice)}
                  className="text-slate-700"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  Preview XML
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => {
                    handleEditInvoice(selectedInvoice);
                    setSelectedInvoice(null);
                  }}
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Editează
                </Button>
                {selectedInvoice.status === "draft" && (
                  <Button 
                    className="bg-green-600 hover:bg-green-700"
                    onClick={() => {
                      handleEmitInvoice(selectedInvoice.id);
                      setSelectedInvoice(null);
                    }}
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Emite Factura
                  </Button>
                )}
                {selectedInvoice.status === "emisa" && !selectedInvoice.anaf_status && anafSettings.is_configured && (
                  <Button 
                    className="bg-teal-600 hover:bg-teal-700"
                    onClick={() => {
                      handleSendToAnaf(selectedInvoice.id);
                      setSelectedInvoice(null);
                    }}
                    disabled={sendingToAnaf === selectedInvoice.id}
                  >
                    {sendingToAnaf === selectedInvoice.id ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Globe className="w-4 h-4 mr-2" />
                    )}
                    Trimite la ANAF
                  </Button>
                )}
                {selectedInvoice.anaf_status && selectedInvoice.anaf_status !== "acceptata" && (
                  <Button 
                    variant="outline"
                    onClick={() => handleCheckAnafStatus(selectedInvoice.id)}
                    disabled={checkingAnafStatus === selectedInvoice.id}
                    className="text-amber-700 border-amber-300"
                  >
                    {checkingAnafStatus === selectedInvoice.id ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4 mr-2" />
                    )}
                    Status ANAF
                  </Button>
                )}
                {selectedInvoice.status === "emisa" && (
                  <Button 
                    className="bg-blue-600 hover:bg-blue-700"
                    onClick={() => {
                      handleMarkPaid(selectedInvoice.id);
                      setSelectedInvoice(null);
                    }}
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Marchează Plătită
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Client Nou/Editare */}
      <Dialog open={showClientForm} onOpenChange={(open) => {
        setShowClientForm(open);
        if (!open) {
          setEditingClient(null);
          setNewClient({ name: "", cui: "", reg_com: "", address: "", city: "", county: "", phone: "", email: "" });
        }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-purple-600" />
              {editingClient ? "Editare Client" : "Adaugă Client Nou"}
            </DialogTitle>
            <DialogDescription>
              Introduceți CUI-ul și datele se vor completa automat
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>CUI *</Label>
                <div className="flex gap-2">
                  <Input 
                    value={editingClient?.cui || newClient.cui}
                    onChange={(e) => {
                      const val = e.target.value.replace(/[^0-9]/g, '');
                      if (editingClient) {
                        setEditingClient({...editingClient, cui: val});
                      } else {
                        setNewClient({...newClient, cui: val});
                      }
                    }}
                    placeholder="12345678"
                  />
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="icon"
                    onClick={async () => {
                      const cui = editingClient?.cui || newClient.cui;
                      if (!cui) return;
                      try {
                        const res = await axios.get(`${API}/clients/lookup/${cui}?company_id=${companyId}`);
                        const { data } = res.data;
                        if (data) {
                          const updates = {
                            name: data.name || data.denumire || "",
                            address: data.address || data.adresa || "",
                            city: data.city || data.localitate || "",
                            county: data.county || data.judet || "",
                            reg_com: data.reg_com || data.numar_reg_com || ""
                          };
                          if (editingClient) {
                            setEditingClient(prev => ({...prev, ...updates}));
                          } else {
                            setNewClient(prev => ({...prev, ...updates}));
                          }
                          toast.success("Date firmă preluate!");
                        }
                      } catch (e) {
                        toast.error("CUI negăsit");
                      }
                    }}
                    title="Caută date după CUI"
                  >
                    <Search className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <div>
                <Label>Nr. Reg. Comerț</Label>
                <Input 
                  value={editingClient?.reg_com || newClient.reg_com}
                  onChange={(e) => {
                    if (editingClient) {
                      setEditingClient({...editingClient, reg_com: e.target.value});
                    } else {
                      setNewClient({...newClient, reg_com: e.target.value});
                    }
                  }}
                  placeholder="J40/1234/2020"
                />
              </div>
            </div>
            
            <div>
              <Label>Denumire Firmă *</Label>
              <Input 
                value={editingClient?.name || newClient.name}
                onChange={(e) => {
                  if (editingClient) {
                    setEditingClient({...editingClient, name: e.target.value});
                  } else {
                    setNewClient({...newClient, name: e.target.value});
                  }
                }}
                placeholder="S.C. Exemplu S.R.L."
              />
            </div>
            
            <div>
              <Label>Adresă</Label>
              <Input 
                value={editingClient?.address || newClient.address}
                onChange={(e) => {
                  if (editingClient) {
                    setEditingClient({...editingClient, address: e.target.value});
                  } else {
                    setNewClient({...newClient, address: e.target.value});
                  }
                }}
                placeholder="Str. Exemplu, Nr. 1"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Oraș</Label>
                <Input 
                  value={editingClient?.city || newClient.city}
                  onChange={(e) => {
                    if (editingClient) {
                      setEditingClient({...editingClient, city: e.target.value});
                    } else {
                      setNewClient({...newClient, city: e.target.value});
                    }
                  }}
                  placeholder="București"
                />
              </div>
              <div>
                <Label>Județ</Label>
                <Input 
                  value={editingClient?.county || newClient.county}
                  onChange={(e) => {
                    if (editingClient) {
                      setEditingClient({...editingClient, county: e.target.value});
                    } else {
                      setNewClient({...newClient, county: e.target.value});
                    }
                  }}
                  placeholder="Sector 1"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Telefon</Label>
                <Input 
                  value={editingClient?.phone || newClient.phone}
                  onChange={(e) => {
                    if (editingClient) {
                      setEditingClient({...editingClient, phone: e.target.value});
                    } else {
                      setNewClient({...newClient, phone: e.target.value});
                    }
                  }}
                  placeholder="0722 123 456"
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input 
                  value={editingClient?.email || newClient.email}
                  onChange={(e) => {
                    if (editingClient) {
                      setEditingClient({...editingClient, email: e.target.value});
                    } else {
                      setNewClient({...newClient, email: e.target.value});
                    }
                  }}
                  placeholder="contact@exemplu.ro"
                />
              </div>
            </div>
            
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => {
                setShowClientForm(false);
                setEditingClient(null);
              }}>
                Anulează
              </Button>
              <Button onClick={handleSaveClientFinal} className="bg-purple-600 hover:bg-purple-700">
                {editingClient ? <Edit className="w-4 h-4 mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                {editingClient ? "Salvează Modificări" : "Salvează Client"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Editare Factură */}

      {/* Dialog Verificare ANAF */}
      <Dialog open={showAnafVerifyDialog} onOpenChange={setShowAnafVerifyDialog}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-teal-600" />
              Verificare ANAF
            </DialogTitle>
            <DialogDescription>
              Date oficiale preluate din registrul ANAF via demoanaf.ro
            </DialogDescription>
          </DialogHeader>

          {verifyingAnafCui ? (
            <div className="flex items-center justify-center py-8 gap-3">
              <Loader2 className="w-6 h-6 animate-spin text-teal-600" />
              <span className="text-slate-500">Se verifică CUI-ul...</span>
            </div>
          ) : anafVerifyResult?.error ? (
            <div className="text-center py-6 space-y-2">
              <XCircle className="w-10 h-10 text-red-400 mx-auto" />
              <p className="text-red-600 font-medium">{anafVerifyResult.message}</p>
            </div>
          ) : anafVerifyResult ? (
            <div className="space-y-4">
              {/* Status badges */}
              <div className="flex flex-wrap gap-2">
                <Badge data-testid="anaf-status-active" className={anafVerifyResult.inactive ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"}>
                  {anafVerifyResult.inactive ? "INACTIV" : "ACTIV"}
                </Badge>
                <Badge data-testid="anaf-status-tva" className={anafVerifyResult.vat_registered ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-600"}>
                  {anafVerifyResult.vat_registered ? "Plătitor TVA" : "Neplătitor TVA"}
                </Badge>
                {anafVerifyResult.efactura_registered && (
                  <Badge className="bg-teal-100 text-teal-700">e-Factura</Badge>
                )}
                {anafVerifyResult.split_vat && (
                  <Badge className="bg-orange-100 text-orange-700">TVA Split</Badge>
                )}
                {anafVerifyResult.cash_basis_vat && (
                  <Badge className="bg-purple-100 text-purple-700">TVA Încasare</Badge>
                )}
              </div>

              {/* Company info */}
              <div className="space-y-3 text-sm">
                <div className="grid grid-cols-[120px_1fr] gap-1">
                  <span className="text-slate-500 font-medium">Denumire:</span>
                  <span data-testid="anaf-company-name" className="font-semibold">{anafVerifyResult.name}</span>
                </div>
                <div className="grid grid-cols-[120px_1fr] gap-1">
                  <span className="text-slate-500 font-medium">CUI:</span>
                  <span>{anafVerifyResult.cui}</span>
                </div>
                <div className="grid grid-cols-[120px_1fr] gap-1">
                  <span className="text-slate-500 font-medium">Nr. Reg. Com.:</span>
                  <span>{anafVerifyResult.registration_number || "-"}</span>
                </div>
                <div className="grid grid-cols-[120px_1fr] gap-1">
                  <span className="text-slate-500 font-medium">Adresă:</span>
                  <span>{anafVerifyResult.address || "-"}</span>
                </div>
                {anafVerifyResult.headquarters?.locality && (
                  <div className="grid grid-cols-[120px_1fr] gap-1">
                    <span className="text-slate-500 font-medium">Localitate:</span>
                    <span>{anafVerifyResult.headquarters.locality}{anafVerifyResult.headquarters.county ? `, ${anafVerifyResult.headquarters.county}` : ""}</span>
                  </div>
                )}
                <div className="grid grid-cols-[120px_1fr] gap-1">
                  <span className="text-slate-500 font-medium">Formă juridică:</span>
                  <span>{anafVerifyResult.legal_form || "-"}</span>
                </div>
                <div className="grid grid-cols-[120px_1fr] gap-1">
                  <span className="text-slate-500 font-medium">CAEN:</span>
                  <span>{anafVerifyResult.caen_code || "-"}</span>
                </div>
                <div className="grid grid-cols-[120px_1fr] gap-1">
                  <span className="text-slate-500 font-medium">Data înreg.:</span>
                  <span>{anafVerifyResult.registration_date || "-"}</span>
                </div>
                <div className="grid grid-cols-[120px_1fr] gap-1">
                  <span className="text-slate-500 font-medium">Admin. fiscal:</span>
                  <span>{anafVerifyResult.fiscal_authority || "-"}</span>
                </div>
                {anafVerifyResult.administrators?.length > 0 && (
                  <div className="grid grid-cols-[120px_1fr] gap-1">
                    <span className="text-slate-500 font-medium">Administratori:</span>
                    <span>{anafVerifyResult.administrators.map(a => a.name).join(", ")}</span>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-2 text-xs text-slate-400 pt-2 border-t">
                <Info className="w-3 h-3" />
                Sursa: demoanaf.ro (registrul ANAF)
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Dialog Editare Factură */}
      <Dialog open={showEditInvoice} onOpenChange={(open) => {
        setShowEditInvoice(open);
        if (!open) setEditingInvoice(null);
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-purple-600" />
              Editare Factură {editingInvoice?.series}/{editingInvoice?.number}
            </DialogTitle>
            {editingInvoice?.status !== "draft" && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-2">
                <p className="text-amber-800 text-sm font-medium flex items-center gap-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  ATENȚIE: Este interzis editarea după trimitere către client!
                </p>
                <p className="text-amber-700 text-xs mt-1">
                  Dacă efectuați modificări aveți obligația de a anunța clientul despre acestea.
                </p>
              </div>
            )}
          </DialogHeader>
          
          {editingInvoice && (
            <div className="space-y-4">
              {/* Date factură */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Data Emiterii *</Label>
                  <Input 
                    type="date"
                    value={editingInvoice.invoice_date}
                    onChange={(e) => setEditingInvoice({...editingInvoice, invoice_date: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Data Scadență</Label>
                  <Input 
                    type="date"
                    value={editingInvoice.due_date || ""}
                    onChange={(e) => setEditingInvoice({...editingInvoice, due_date: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Status</Label>
                  <Input value={editingInvoice.status === "draft" ? "Ciornă" : editingInvoice.status === "emisa" ? "Emisă" : "Plătită"} disabled className="bg-slate-100" />
                </div>
              </div>

              {/* Date client */}
              <div className="border-t pt-4">
                <h4 className="font-semibold mb-3">Date Client</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>CUI Client *</Label>
                    <Input 
                      value={editingInvoice.client_cui || ""}
                      onChange={(e) => setEditingInvoice({...editingInvoice, client_cui: e.target.value.toUpperCase()})}
                      placeholder="12345678"
                    />
                  </div>
                  <div>
                    <Label>Nume Client *</Label>
                    <Input 
                      value={editingInvoice.client_name || ""}
                      onChange={(e) => setEditingInvoice({...editingInvoice, client_name: e.target.value})}
                      placeholder="Denumire firmă client"
                    />
                  </div>
                  <div>
                    <Label>Nr. Reg. Comerț</Label>
                    <Input 
                      value={editingInvoice.client_reg_com || ""}
                      onChange={(e) => setEditingInvoice({...editingInvoice, client_reg_com: e.target.value})}
                      placeholder="J40/1234/2020"
                    />
                  </div>
                  <div className="col-span-3">
                    <Label>Adresă Client</Label>
                    <Input 
                      value={editingInvoice.client_address || ""}
                      onChange={(e) => setEditingInvoice({...editingInvoice, client_address: e.target.value})}
                      placeholder="Adresa completă"
                    />
                  </div>
                </div>
              </div>

              {/* Articole */}
              <div className="border-t pt-4">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="font-semibold">Articole</h4>
                  <Button size="sm" variant="outline" onClick={addEditItem}>
                    <Plus className="w-4 h-4 mr-1" />
                    Articol
                  </Button>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[300px]">Denumire</TableHead>
                      <TableHead>U.M.</TableHead>
                      <TableHead>Cant.</TableHead>
                      <TableHead>Preț unit.</TableHead>
                      <TableHead>TVA %</TableHead>
                      <TableHead>Total</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {editingInvoice.items.map((item, idx) => (
                      <TableRow key={`edit-item-${idx}-${item.name}`}>
                        <TableCell>
                          <Input 
                            value={item.name || ""}
                            onChange={(e) => updateEditItem(idx, 'name', e.target.value)}
                            placeholder="Denumire produs/serviciu"
                          />
                        </TableCell>
                        <TableCell>
                          <Input 
                            value={item.um || "buc"}
                            onChange={(e) => updateEditItem(idx, 'um', e.target.value)}
                            className="w-16"
                          />
                        </TableCell>
                        <TableCell>
                          <Input 
                            type="number"
                            value={item.quantity || 0}
                            onChange={(e) => updateEditItem(idx, 'quantity', parseFloat(e.target.value) || 0)}
                            className="w-20"
                          />
                        </TableCell>
                        <TableCell>
                          <Input 
                            type="number"
                            value={item.unit_price || 0}
                            onChange={(e) => updateEditItem(idx, 'unit_price', parseFloat(e.target.value) || 0)}
                            className="w-24"
                          />
                        </TableCell>
                        <TableCell>
                          <Input 
                            type="number"
                            value={item.vat_percent || 21}
                            onChange={(e) => updateEditItem(idx, 'vat_percent', parseFloat(e.target.value) || 0)}
                            className="w-16"
                          />
                        </TableCell>
                        <TableCell className="font-medium">
                          {formatNumber((item.quantity || 0) * (item.unit_price || 0))} RON
                        </TableCell>
                        <TableCell>
                          {editingInvoice.items.length > 1 && (
                            <Button size="sm" variant="ghost" onClick={() => removeEditItem(idx)} className="text-red-500">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {/* Totaluri */}
                <div className="flex justify-end mt-4">
                  <div className="w-64 space-y-2">
                    {(() => {
                      const editTotals = calculateEditTotals(editingInvoice.items);
                      return (
                        <>
                          <div className="flex justify-between">
                            <span>Subtotal:</span>
                            <span className="font-medium">{formatNumber(editTotals.subtotal)} RON</span>
                          </div>
                          <div className="flex justify-between">
                            <span>TVA:</span>
                            <span className="font-medium">{formatNumber(editTotals.vatAmount)} RON</span>
                          </div>
                          <div className="flex justify-between text-lg font-bold border-t pt-2">
                            <span>TOTAL:</span>
                            <span>{formatNumber(editTotals.total)} RON</span>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>

              {/* Note */}
              <div>
                <Label>Observații</Label>
                <Textarea 
                  value={editingInvoice.notes || ""}
                  onChange={(e) => setEditingInvoice({...editingInvoice, notes: e.target.value})}
                  placeholder="Observații suplimentare..."
                  rows={2}
                />
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => {
                  setShowEditInvoice(false);
                  setEditingInvoice(null);
                }}>
                  Anulează
                </Button>
                <Button onClick={handleSaveEditedInvoice} className="bg-purple-600 hover:bg-purple-700">
                  <Edit className="w-4 h-4 mr-2" />
                  Salvează Modificări
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Setări ANAF e-Factura */}
      <Dialog open={showAnafSettings} onOpenChange={setShowAnafSettings}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Globe className="w-5 h-5 text-teal-600" />
              Setări ANAF e-Factura
            </DialogTitle>
            <DialogDescription>
              Configurați conexiunea cu sistemul ANAF e-Factura pentru trimiterea automată a facturilor
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Connection Status */}
            <div className={`p-4 rounded-lg border ${anafSettings.is_configured ? (anafSettings.anaf_environment === 'production' ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-300') : 'bg-amber-50 border-amber-200'}`}>
              <div className="flex items-center gap-2">
                {anafSettings.is_configured ? (
                  <>
                    {anafSettings.anaf_environment === 'production' ? (
                      <>
                        <Shield className="w-5 h-5 text-green-600" />
                        <span className="font-medium text-green-800">Conectat la ANAF — PRODUCȚIE</span>
                      </>
                    ) : (
                      <>
                        <AlertTriangle className="w-5 h-5 text-amber-600" />
                        <span className="font-medium text-amber-900">Conectat la ANAF — MOD TEST (simulare)</span>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <AlertTriangle className="w-5 h-5 text-amber-600" />
                    <span className="font-medium text-amber-800">ANAF nu este configurat</span>
                  </>
                )}
              </div>
              <p className="text-sm mt-1 text-slate-600">
                {anafSettings.is_configured 
                  ? `Mediu: ${anafSettings.anaf_environment === 'production' ? 'Producție (facturi reale)' : 'Test (simulare — nu ajunge la beneficiar)'} | CUI: ${anafSettings.anaf_cui}`
                  : 'Adăugați token-ul OAuth2 și CUI-ul pentru a activa trimiterea facturilor către ANAF.'
                }
              </p>
              {anafSettings.is_configured && anafSettings.last_test_at && (
                <p className={`text-xs mt-2 ${anafSettings.last_test_success ? 'text-green-700' : 'text-red-700'}`}>
                  {anafSettings.last_test_success ? '✓' : '✗'} Ultim test: {new Date(anafSettings.last_test_at).toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  {!anafSettings.last_test_success && anafSettings.last_test_message ? ` — ${anafSettings.last_test_message}` : ''}
                </p>
              )}
            </div>

            {/* Help: Cum obțin tokenul */}
            <div className="rounded-lg border border-blue-200 bg-blue-50">
              <button
                type="button"
                onClick={() => setShowAnafHelp(!showAnafHelp)}
                className="w-full flex items-center justify-between p-3 text-left"
                data-testid="anaf-help-toggle"
              >
                <span className="flex items-center gap-2 text-blue-800 font-medium text-sm">
                  <Info className="w-4 h-4" />
                  Cum obțin tokenul OAuth2 de la ANAF?
                </span>
                <span className="text-blue-700 text-xs">{showAnafHelp ? '▲ ascunde' : '▼ vezi pași'}</span>
              </button>
              {showAnafHelp && (
                <div className="px-4 pb-4 text-xs sm:text-sm text-slate-700 space-y-2 border-t border-blue-200 pt-3">
                  <p><strong>Ai nevoie de un certificat digital calificat</strong> (token USB de la certSign / AlfaSign / DigiSign / Trans Sped) emis pe CUI-ul firmei și înregistrat ca reprezentant la ANAF (formular 150).</p>
                  <ol className="list-decimal list-inside space-y-1 ml-1">
                    <li>Intră pe portalul ANAF de înregistrare aplicații OAuth2:&nbsp;
                      <a className="text-blue-700 underline inline-flex items-center gap-1" href="https://logincert.anaf.ro/anaf-oauth2/v1/manage" target="_blank" rel="noreferrer">
                        logincert.anaf.ro <ExternalLink className="w-3 h-3" />
                      </a>
                    </li>
                    <li>Autentifică-te cu certificatul digital (token USB conectat).</li>
                    <li>Creează o aplicație nouă (Nume aplicație, Callback URL — orice URL valid HTTPS).</li>
                    <li>Notează <strong>client_id</strong> și <strong>client_secret</strong>.</li>
                    <li>Generează un <strong>access_token</strong> (valabil 90 zile). Acesta este tokenul pe care îl pui în câmpul „Token OAuth2 ANAF" mai jos.</li>
                    <li>Începe în mediul <strong>Test</strong>, validează cu „Testează Conexiunea", apoi comută pe „Producție".</li>
                  </ol>
                  <p className="text-amber-800 mt-2">⚠️ <strong>NU</strong> folosi parola contului tău de e-Tesa sau alte parole — ANAF acceptă strict tokenul OAuth2 obținut prin pașii de mai sus, altfel primești eroare 401.</p>
                  <p className="text-slate-600">Documentație oficială:&nbsp;
                    <a className="text-blue-700 underline" href="https://mfinante.gov.ro/static/10/Mfp/efactura/Oauth_procedura_inregistrare_aplicatii_portal_ANAF.pdf" target="_blank" rel="noreferrer">
                      procedura ANAF (PDF)
                    </a>
                  </p>
                </div>
              )}
            </div>

            {/* Credentials */}
            <div className="space-y-4">
              <h4 className="font-semibold flex items-center gap-2">
                <Shield className="w-4 h-4" />
                Credențiale ANAF
              </h4>
              
              <div>
                <Label>CUI Companie (fără prefix RO) *</Label>
                <Input 
                  value={anafSettings.anaf_cui || ""}
                  onChange={(e) => setAnafSettings({...anafSettings, anaf_cui: e.target.value})}
                  placeholder="39809164"
                  data-testid="anaf-cui-input"
                />
                <p className="text-xs text-slate-500 mt-1">CUI-ul companiei înregistrate în sistemul e-Factura ANAF</p>
              </div>

              {/* OAuth2 connect card */}
              <div className={`rounded-lg border-2 p-4 ${anafOauthStatus?.connected ? 'bg-emerald-50 border-emerald-300' : 'bg-blue-50 border-blue-300'}`}>
                <div className="flex items-start gap-3">
                  <Shield className={`w-6 h-6 shrink-0 mt-0.5 ${anafOauthStatus?.connected ? 'text-emerald-600' : 'text-blue-600'}`} />
                  <div className="flex-1 min-w-0">
                    <h5 className={`font-semibold ${anafOauthStatus?.connected ? 'text-emerald-800' : 'text-blue-800'}`}>
                      Conectare OAuth2 ANAF
                      {anafOauthStatus?.connected && <span className="ml-2 text-xs bg-emerald-600 text-white px-2 py-0.5 rounded-full">Conectat</span>}
                    </h5>
                    <p className="text-xs text-slate-600 mt-1 leading-relaxed">
                      Conectează-te cu certificatul tău digital ANAF — fără să mai generezi manual un Bearer token. Reîmprospătare automată la 90 zile.
                    </p>
                    {anafOauthStatus?.connected && anafOauthStatus?.expires_at && (
                      <p className="text-xs text-emerald-700 mt-2">
                        Token valid până la <strong>{new Date(anafOauthStatus.expires_at).toLocaleString("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                        {anafOauthStatus.days_left != null && <span className="ml-1">({anafOauthStatus.days_left} zile rămase)</span>}
                      </p>
                    )}
                    {anafOauthStatus && !anafOauthStatus.configured && (
                      <p className="text-xs text-amber-700 mt-2">
                        ⚠ OAuth nu este configurat pe server. Administratorul platformei trebuie să seteze <code>ANAF_OAUTH_CLIENT_ID</code>, <code>ANAF_OAUTH_CLIENT_SECRET</code> și <code>ANAF_OAUTH_REDIRECT_URI</code> în .env.
                      </p>
                    )}
                    <div className="flex flex-wrap gap-2 mt-3">
                      {!anafOauthStatus?.connected ? (
                        <Button
                          size="sm"
                          onClick={handleConnectAnafOauth}
                          disabled={!anafOauthStatus?.configured}
                          className="bg-blue-600 hover:bg-blue-700 text-white"
                          data-testid="anaf-oauth-connect-btn"
                        >
                          <Shield className="w-4 h-4 mr-1.5" />
                          Conectează-te ANAF (OAuth2)
                        </Button>
                      ) : (
                        <>
                          <Button
                            size="sm" variant="outline"
                            onClick={handleRefreshAnafToken}
                            className="border-emerald-300 text-emerald-700 hover:bg-emerald-100"
                            data-testid="anaf-oauth-refresh-btn"
                          >
                            Reîmprospătează token
                          </Button>
                          <Button
                            size="sm" variant="outline"
                            onClick={handleDisconnectAnafOauth}
                            className="border-red-300 text-red-700 hover:bg-red-50"
                            data-testid="anaf-oauth-disconnect-btn"
                          >
                            Deconectează
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <Label>Token OAuth2 ANAF (manual) — opțional</Label>
                <Input 
                  type="password"
                  value={anafSettings.anaf_token || ""}
                  onChange={(e) => setAnafSettings({...anafSettings, anaf_token: e.target.value})}
                  placeholder="Introduceți tokenul OAuth2 de la ANAF SPV"
                  data-testid="anaf-token-input"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Obțineți tokenul din portalul ANAF SPV (Spațiul Privat Virtual). 
                  Consultați documentația ANAF pentru pașii de înregistrare a aplicației.
                </p>
                {anafSettings.anaf_token_masked && (
                  <p className="text-xs text-green-600 mt-1">Token actual: {anafSettings.anaf_token_masked}</p>
                )}
              </div>
              
              <div>
                <Label>Mediu</Label>
                <Select 
                  value={anafSettings.anaf_environment || "test"}
                  onValueChange={(v) => setAnafSettings({...anafSettings, anaf_environment: v})}
                >
                  <SelectTrigger data-testid="anaf-environment-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="test">Test (Recomandare inițială)</SelectItem>
                    <SelectItem value="production">Producție (Facturi reale)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1">
                  Începeți cu mediul de Test. Comutați pe Producție doar după ce ați validat funcționarea.
                </p>
              </div>
            </div>

            {/* Auto-send settings */}
            <div className="space-y-4 border-t pt-4">
              <h4 className="font-semibold flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Trimitere Automată
              </h4>
              
              <div className="flex items-center gap-3">
                <input 
                  type="checkbox"
                  checked={anafSettings.auto_send_enabled || false}
                  onChange={(e) => setAnafSettings({...anafSettings, auto_send_enabled: e.target.checked})}
                  className="w-4 h-4 text-teal-600 rounded"
                  data-testid="anaf-auto-send-checkbox"
                />
                <div>
                  <Label className="cursor-pointer">Activează trimiterea automată</Label>
                  <p className="text-xs text-slate-500">Facturile emise vor fi trimise automat la ANAF după numărul de zile configurat</p>
                </div>
              </div>
              
              {anafSettings.auto_send_enabled && (
                <div>
                  <Label>Trimite automat după (zile)</Label>
                  <Input 
                    type="number"
                    min={1}
                    max={5}
                    value={anafSettings.auto_send_days || 5}
                    onChange={(e) => setAnafSettings({...anafSettings, auto_send_days: parseInt(e.target.value) || 5})}
                    className="w-24"
                    data-testid="anaf-auto-send-days"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Conform legislației, facturile trebuie trimise în maxim 5 zile calendaristice de la emitere.
                  </p>
                </div>
              )}
            </div>

            {/* Preluare facturi de pe SPV (verificare reală a conexiunii) */}
            {anafSettings.is_configured && (
              <div className="space-y-3 border-t pt-4">
                <h4 className="font-semibold flex items-center gap-2">
                  <CloudUpload className="w-4 h-4 rotate-180" />
                  Preluare facturi de pe SPV (test conexiune reală)
                </h4>
                <p className="text-xs text-slate-600">
                  Apasă „Preia acum" pentru a aduce facturile primite în SPV din ultimele zile selectate.
                  Dacă apar facturi reale ale firmei tale (în mediu Producție), conexiunea este 100% funcțională.
                  {anafSettings.anaf_environment !== 'production' && (
                    <span className="block text-amber-700 mt-1">
                      ⚠️ În MOD TEST, vei vedea doar facturi simulate ANAF — nu cele reale ale firmei.
                    </span>
                  )}
                </p>
                <div className="flex items-end gap-2 flex-wrap">
                  <div>
                    <Label className="text-xs">Ultimele zile</Label>
                    <Input
                      type="number"
                      min={1}
                      max={60}
                      value={syncSpvDays}
                      onChange={(e) => setSyncSpvDays(parseInt(e.target.value) || 30)}
                      className="w-24"
                      data-testid="anaf-spv-days-input"
                    />
                  </div>
                  <Button
                    variant="outline"
                    onClick={handleSyncReceivedFromSpv}
                    disabled={syncingSpv}
                    className="border-teal-400 text-teal-700 hover:bg-teal-50"
                    data-testid="anaf-sync-spv-btn"
                  >
                    {syncingSpv ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                    {syncingSpv ? 'Preluare...' : 'Preia acum'}
                  </Button>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-between gap-2 pt-4 border-t flex-wrap">
              <div className="flex gap-2 flex-wrap">
                <Button 
                  variant="outline" 
                  onClick={handleTestAnafConnection}
                  disabled={!anafSettings.anaf_token}
                  className="text-teal-700"
                  data-testid="anaf-test-connection-btn"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Testează Conexiunea
                </Button>
                {anafSettings.is_configured && (
                  <Button
                    variant="outline"
                    onClick={handleDisconnectAnaf}
                    className="border-red-300 text-red-700 hover:bg-red-50"
                    data-testid="anaf-disconnect-btn"
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Deconectare ANAF
                  </Button>
                )}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowAnafSettings(false)}>Anulează</Button>
                <Button onClick={handleSaveAnafSettings} className="bg-teal-600 hover:bg-teal-700" data-testid="anaf-save-settings-btn">
                  Salvează Setări ANAF
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Preview XML e-Factura */}
      <Dialog open={showXmlPreview} onOpenChange={setShowXmlPreview}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Code className="w-5 h-5 text-teal-600" />
              Preview XML e-Factura
              {xmlPreviewData && (
                <span className="text-sm font-normal text-slate-500">
                  {xmlPreviewData.series}/{xmlPreviewData.number}
                </span>
              )}
            </DialogTitle>
            <DialogDescription>Structura XML UBL 2.1 conform standardului ANAF RO CIUS</DialogDescription>
          </DialogHeader>
          
          {xmlPreviewData && (
            <div className="space-y-4">
              {/* Validation Results */}
              <div className={`p-3 rounded-lg border ${xmlPreviewData.validation?.valid ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                <div className="flex items-center gap-2">
                  {xmlPreviewData.validation?.valid ? (
                    <>
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <span className="font-medium text-green-800">XML valid - gata pentru trimitere la ANAF</span>
                    </>
                  ) : (
                    <>
                      <AlertTriangle className="w-5 h-5 text-red-600" />
                      <span className="font-medium text-red-800">XML are erori de validare</span>
                    </>
                  )}
                </div>
                {xmlPreviewData.validation?.errors?.length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {xmlPreviewData.validation.errors.map((err) => (
                      <li key={err} className="text-sm text-red-700 flex items-center gap-1">
                        <span className="w-1.5 h-1.5 bg-red-500 rounded-full flex-shrink-0" />
                        {err}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              
              {/* XML Content */}
              <div className="relative">
                <pre className="bg-slate-900 text-green-400 p-4 rounded-lg text-xs overflow-auto max-h-[500px] font-mono">
                  {xmlPreviewData.xml}
                </pre>
                <Button 
                  size="sm"
                  variant="ghost"
                  className="absolute top-2 right-2 text-slate-400 hover:text-white hover:bg-slate-700"
                  onClick={() => {
                    navigator.clipboard.writeText(xmlPreviewData.xml);
                    toast.success("XML copiat!");
                  }}
                >
                  Copiază
                </Button>
              </div>
              
              {/* Actions */}
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowXmlPreview(false)}>Închide</Button>
                <Button 
                  onClick={() => {
                    handleDownloadXML({ id: xmlPreviewData.invoice_id, series: xmlPreviewData.series, number: xmlPreviewData.number });
                    setShowXmlPreview(false);
                  }}
                  className="bg-teal-600 hover:bg-teal-700"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Descarcă XML
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

